'use client';

import React, { Fragment, useEffect, useState, useRef, useMemo } from 'react';
import {
  Column,
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
  Row,
  RowData,
  ExpandedState,
  ColumnSizingState,
  ColumnOrderState,
  ColumnPinningState,
  RowSelectionState,
} from '@tanstack/react-table';
import { cn } from '@/app/lib/utils';
import Dropdown from './Dropdown';
import Icon from './Icon';
import { CustomScroll } from './CustomScroll';
import StatusCard from './StatusCard';
import Spinner from './Spinner';
import { Button } from './Button';
import { EllipsisVertical } from 'lucide-react';
import { TableSkeletonRow, type TableSkeletonConfig } from './TableSkeleton';

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    columnClassName?: string;
    isError?: boolean;
    error?: string;
    showFooter?: boolean;
    showHeaderDropdown?: boolean;
    inlineEdit?: boolean;
    clickable?: boolean;
    isActionColumn?: boolean;
    isUtilityColumn?: boolean;
    skeleton?: TableSkeletonConfig;
    width?: string | number;
    pixelWidth?: boolean;
  }
}

const getColumnId = <T extends RowData>(column: ColumnDef<T>): string | null => {
  if (column.id) return column.id;
  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return column.accessorKey;
  }
  return null;
};

const isUtilityColumn = <T extends RowData>(column: ColumnDef<T> | Column<T, unknown>): boolean => {
  if ('columnDef' in column) {
    const id = column.id;
    return column.columnDef.meta?.isUtilityColumn === true || id === 'select' || id === 'index';
  }
  const id = getColumnId(column);
  return column.meta?.isUtilityColumn === true || id === 'select' || id === 'index';
};

const isActionColumn = <T extends RowData>(column: ColumnDef<T> | Column<T, unknown>): boolean => {
  if ('columnDef' in column) {
    const id = column.id;
    return column.columnDef.meta?.isActionColumn === true || id === 'action' || id === 'actions';
  }
  const id = getColumnId(column);
  return column.meta?.isActionColumn === true || id === 'action' || id === 'actions';
};

const normalizeColumnOrder = (
  order: string[],
  declaredOrder: string[],
  actionColumnIds: string[]
): string[] => {
  const validOrder = order.filter(
    (id, index) => declaredOrder.includes(id) && order.indexOf(id) === index
  );
  if (validOrder.length !== declaredOrder.length) {
    return declaredOrder;
  }
  const actionIds = new Set(actionColumnIds);

  return [
    ...validOrder.filter(id => !actionIds.has(id)),
    ...declaredOrder.filter(id => actionIds.has(id)),
  ];
};

export interface TableProps<
  T extends RowData = unknown,
> extends React.TableHTMLAttributes<HTMLTableElement> {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (
    data: T,
    row: Row<T>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void;
  showHeader?: boolean;
  headerClassName?: string;
  rowClassName?: string | ((row: Row<T>, index: number) => string);
  isError?: boolean;
  isLoading?: boolean;
  skeletonRowCount?: number;
  emptySkeletonRowCount?: number;
  noDataText?: string;
  noDataDescription?: string;
  emptyStateClassName?: string;
  emptyStateType?: 'empty' | 'error';
  error?: string;
  showFooter?: boolean;
  columnSizingEnabled?: boolean;
  renderAdditionalRowInfo?: (row: Row<T>, index: number) => React.ReactNode;
  renderExpandedRowInfo?: (row: Row<T>, index: number) => React.ReactNode;
  getRowCanExpand?: (row: Row<T>) => boolean;
  tableId?: string;
  wrapperClassName?: string;
  defaultPinning?: {
    left?: string[];
    right?: string[];
  };
  showHeaderDropdown?: boolean;
  orientation?: 'vertical' | 'horizontal' | 'both';
  infiniteScroll?: boolean;
  hasMore?: boolean;
  loading?: boolean;
  onLoadMore?: () => void;
  scrollEnabled?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (state: RowSelectionState) => void;
  enableRowSelection?: boolean;
  isCardWrapper?: boolean;
}

/**
 * Table component
 */
const Table = <T,>({
  columns,
  data,
  wrapperClassName,
  className,
  headerClassName,
  rowClassName,
  showHeader = true,
  isError = false,
  error,
  onRowClick,
  noDataText = 'No data to show',
  noDataDescription = 'No data to show',
  emptyStateClassName,
  emptyStateType = 'empty',
  showFooter,
  renderAdditionalRowInfo,
  renderExpandedRowInfo,
  getRowCanExpand,
  columnSizingEnabled,
  tableId,
  defaultPinning,
  showHeaderDropdown = true,
  isLoading = false,
  skeletonRowCount = 0,
  emptySkeletonRowCount = 0,
  orientation = 'both',
  infiniteScroll,
  hasMore,
  loading,
  onLoadMore,
  scrollEnabled = true,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection = false,
  isCardWrapper = false,
}: TableProps<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const tableRef = useRef<HTMLTableElement>(null);

  const declaredColumnOrder = React.useMemo(
    () => columns.map(getColumnId).filter((id): id is string => id !== null),
    [columns]
  );
  const actionColumnIds = React.useMemo(
    () =>
      columns
        .filter(isActionColumn)
        .map(getColumnId)
        .filter((id): id is string => id !== null),
    [columns]
  );
  const columnOrderHydrationKey = `${tableId || ''}:${declaredColumnOrder.join('|')}`;
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(declaredColumnOrder);
  const [hydratedColumnOrderKey, setHydratedColumnOrderKey] = useState('');
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: defaultPinning?.left || [],
    right: defaultPinning?.right || [],
  });

  useEffect(() => {
    if (!columnSizingEnabled || !tableId) return;

    const saved = localStorage.getItem(`${tableId}-columnSizing`);
    if (saved) {
      try {
        setColumnSizing(JSON.parse(saved));
      } catch {
        console.warn('Invalid columnSizing data');
      }
    }
  }, [columnSizingEnabled, tableId]);

  useEffect(() => {
    if (columnSizingEnabled && columnSizing && tableId) {
      localStorage.setItem(`${tableId}-columnSizing`, JSON.stringify(columnSizing));
    }
  }, [columnSizingEnabled, columnSizing, tableId]);

  useEffect(() => {
    let nextOrder = declaredColumnOrder;

    if (tableId) {
      const saved = localStorage.getItem(`${tableId}-columnOrder`);
      if (saved) {
        try {
          nextOrder = JSON.parse(saved);
        } catch {
          console.warn('Invalid columnOrder data');
        }
      }
    }

    setColumnOrder(normalizeColumnOrder(nextOrder, declaredColumnOrder, actionColumnIds));
    setHydratedColumnOrderKey(columnOrderHydrationKey);
  }, [columnOrderHydrationKey]);

  useEffect(() => {
    if (columnOrder && tableId && hydratedColumnOrderKey === columnOrderHydrationKey) {
      localStorage.setItem(`${tableId}-columnOrder`, JSON.stringify(columnOrder));
    }
  }, [columnOrder, tableId, hydratedColumnOrderKey, columnOrderHydrationKey]);

  const updateColumnOrder = React.useCallback(
    (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => {
      setColumnOrder(currentOrder => {
        const nextOrder = typeof updater === 'function' ? updater(currentOrder) : updater;
        return normalizeColumnOrder(nextOrder, declaredColumnOrder, actionColumnIds);
      });
    },
    [declaredColumnOrder, actionColumnIds]
  );

  useEffect(() => {
    if (!tableId) return;
    const saved = localStorage.getItem(`${tableId}-columnPinning`);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaultPinning so required pins (e.g. select column) are never lost
      setColumnPinning({
        left: [...new Set([...(defaultPinning?.left || []), ...(parsed.left || [])])],
        right: [...new Set([...(defaultPinning?.right || []), ...(parsed.right || [])])],
      });
    }
  }, [tableId]);

  useEffect(() => {
    setColumnPinning(prev => ({
      left: [
        ...new Set([
          ...(defaultPinning?.left || []),
          ...(prev.left || []).filter(id => defaultPinning?.left?.includes(id)),
        ]),
      ],
      right: [
        ...new Set([
          ...(defaultPinning?.right || []),
          ...(prev.right || []).filter(id => defaultPinning?.right?.includes(id)),
        ]),
      ],
    }));
  }, [JSON.stringify(defaultPinning)]);

  useEffect(() => {
    if (columnPinning && tableId) {
      localStorage.setItem(`${tableId}-columnPinning`, JSON.stringify(columnPinning));
    }
  }, [columnPinning, tableId]);

  const handleColumnSizing = (
    updater: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)
  ) => {
    if (!columnSizingEnabled || !tableRef.current) {
      return;
    }
    const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
    const containerWidth = tableRef.current.parentElement?.offsetWidth || 0;
    const currentTotalSize = getCenterTotalSize();
    let newTotalSize = 0;

    let totalDiff = 0;
    Object.keys(newSizing).forEach(columnId => {
      const newSize = newSizing[columnId];
      const currentSize = columnSizing[columnId] || 0;
      totalDiff += newSize - currentSize;
    });

    newTotalSize = currentTotalSize + totalDiff;

    const isExpanding = totalDiff > 0;

    const wouldMaintainMinWidth = newTotalSize >= containerWidth;

    if (isExpanding || wouldMaintainMinWidth) {
      setColumnSizing(newSizing);
    }
  };

  const hasPercentColumns = useMemo(
    () =>
      columns.some(c => {
        if (isActionColumn(c) || isUtilityColumn(c)) return false;
        return (
          (c as any).meta?.width !== undefined ||
          ((c as any).size !== undefined && (c as any).size <= 100)
        );
      }),
    [columns]
  );

  const { getHeaderGroups, getRowModel, getFooterGroups, getCenterTotalSize } = useReactTable({
    columns,
    data,
    defaultColumn: {
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
      size: 150,
    },
    columnResizeMode: columnSizingEnabled && !hasPercentColumns ? 'onChange' : undefined,
    columnResizeDirection: columnSizingEnabled && !hasPercentColumns ? 'ltr' : undefined,
    onColumnSizingChange:
      columnSizingEnabled && !hasPercentColumns ? handleColumnSizing : undefined,
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: getRowCanExpand,
    onExpandedChange: setExpanded,
    onColumnOrderChange: updateColumnOrder,
    onColumnPinningChange: setColumnPinning,
    enableRowSelection,
    onRowSelectionChange: onRowSelectionChange
      ? updater => {
          const next = typeof updater === 'function' ? updater(rowSelection ?? {}) : updater;
          onRowSelectionChange(next);
        }
      : undefined,
    state: {
      expanded,
      columnOrder: columnOrder,
      columnPinning: columnPinning,
      ...(columnSizingEnabled && !hasPercentColumns && columnSizing ? { columnSizing } : {}),
      ...(enableRowSelection ? { rowSelection: rowSelection ?? {} } : {}),
    },
  });

  const getColStyle = (column: Column<T, unknown>, id: string): React.CSSProperties => {
    const isAction = isActionColumn(column);
    const isUtility = isUtilityColumn(column);
    const actionWidth = isAction ? (column.columnDef.size ?? 80) : undefined;
    const utilityWidth = isUtility ? (column.columnDef.size ?? 42) : undefined;
    const isPinnedLeft = columnPinning.left?.includes(id);
    const isPinnedRight = columnPinning.right?.includes(id);

    const metaWidth = column.columnDef.meta?.width;

    let width: string | number | undefined;
    let minWidth: string | number | undefined;
    let maxWidth: string | number | undefined;

    if (isAction) {
      width = `${actionWidth}px`;
      minWidth = `${actionWidth}px`;
      maxWidth = `${actionWidth}px`;
    } else if (isUtility) {
      width = `${utilityWidth}px`;
      minWidth = `${utilityWidth}px`;
      maxWidth = `${utilityWidth}px`;
    } else {
      if (metaWidth !== undefined) {
        width = typeof metaWidth === 'number' ? `${metaWidth}%` : metaWidth;
      } else if (column.columnDef.size !== undefined && column.columnDef.size <= 100) {
        width = `${column.columnDef.size}%`;
      } else if (columnSizingEnabled && !hasPercentColumns && columnSizing?.[id] !== undefined) {
        width = `${columnSizing[id]}px`;
      } else if (column.columnDef.size !== undefined) {
        width = `${column.columnDef.size}px`;
      } else if (columnSizingEnabled && !hasPercentColumns) {
        width = `${column.getSize()}px`;
      }

      minWidth = undefined;
      maxWidth = 'auto';
    }

    return {
      width,
      minWidth,
      maxWidth,
      left: isPinnedLeft ? `${column.getStart('left')}px` : undefined,
      right: isPinnedRight ? `${column.getAfter('right')}px` : undefined,
    };
  };

  const tableElement = (
    <table
      ref={tableRef}
      id={tableId}
      className={cn('w-full caption-bottom text-sm rounded-[inherit]', className)}
      data-card-wrapper={isCardWrapper ? 'true' : undefined}
      style={{
        tableLayout: columnSizingEnabled && !hasPercentColumns ? 'fixed' : 'auto',
        width: columnSizingEnabled && !hasPercentColumns ? getCenterTotalSize() : undefined,
        minWidth: columnSizingEnabled && !hasPercentColumns ? '100%' : undefined,
      }}
    >
      {showHeader && (
        <thead className={cn('sticky top-0 z-10 rounded-t-[inherit]')}>
          {getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="divide-x divide-border">
              {headerGroup.headers.map((header, index) => (
                <th
                  key={header.id + index}
                  className={cn(
                    'h-10 pl-4 pr-3 whitespace-nowrap text-left align-middle font-normal [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] text-muted-foreground text-sm bg-background relative [box-shadow:inset_0_-1px_0_0_var(--color-border)] [&:not(:last-child)]:border-r [&:not(:last-child)]:border-border',
                    isCardWrapper && 'bg-background dark:bg-muted',
                    'first:rounded-tl-[inherit] last:rounded-tr-[inherit]',
                    headerClassName,
                    header.column.columnDef.meta?.headerClassName,
                    columnPinning.left?.includes(header.id) && 'sticky z-20 sticky-header',
                    columnPinning.right?.includes(header.id) && 'sticky z-20 sticky-right',
                    isActionColumn(header.column) && 'action-column'
                  )}
                  data-action-column={isActionColumn(header.column) ? 'true' : undefined}
                  data-testid={`table-header-${header.id}`}
                  style={getColStyle(header.column, header.id)}
                >
                  {!showHeaderDropdown ||
                  actionColumnIds.includes(header.id) ||
                  header.column.columnDef.meta?.showHeaderDropdown === false ? (
                    flexRender(header.column.columnDef.header, header.getContext())
                  ) : (
                    <div className="flex h-full w-full items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      <Dropdown
                        align="end"
                        matchTriggerWidth={false}
                        contentClassName="min-w-[140px] w-auto"
                        menuItemClassName="whitespace-nowrap"
                        menuItems={[
                          {
                            value: 'freeze',
                            label: (
                              <>
                                <Icon name="square-line" size={5} />
                                <span className="font-medium">
                                  {columnPinning.left?.includes(header.id) ? 'Unfreeze' : 'Freeze'}{' '}
                                  column
                                </span>
                              </>
                            ),
                          },
                          {
                            value: 'move-left',
                            disabled: columnOrder.indexOf(header.id) === 0,
                            label: (
                              <>
                                <Icon name="arrow-left-line" size={5} />
                                <span className="font-medium">Move left</span>
                              </>
                            ),
                          },
                          {
                            value: 'move-right',
                            disabled: columnOrder.indexOf(header.id) === columnOrder.length - 1,
                            label: (
                              <>
                                <Icon name="arrow-left-line" className="rotate-180" size={5} />
                                <span className="font-medium">Move right</span>
                              </>
                            ),
                          },
                        ]}
                        onItemClick={item => {
                          if (item === 'freeze') {
                            setColumnPinning(prev => {
                              const newPinning = { ...prev };
                              if (newPinning.left?.includes(header.id)) {
                                newPinning.left = newPinning.left?.filter(id => id !== header.id);
                              } else {
                                newPinning.left = [...(newPinning.left || []), header.id];
                              }
                              return newPinning;
                            });
                            return;
                          }
                          if (item === 'move-left') {
                            const currentIndex = columnOrder.indexOf(header.id);
                            if (currentIndex > 0) {
                              const newOrder = [...columnOrder];
                              [newOrder[currentIndex], newOrder[currentIndex - 1]] = [
                                newOrder[currentIndex - 1],
                                newOrder[currentIndex],
                              ];
                              updateColumnOrder(newOrder);
                            }
                            return;
                          }
                          if (item === 'move-right') {
                            const currentIndex = columnOrder.indexOf(header.id);
                            if (currentIndex < columnOrder.length - 1) {
                              const newOrder = [...columnOrder];
                              [newOrder[currentIndex], newOrder[currentIndex + 1]] = [
                                newOrder[currentIndex + 1],
                                newOrder[currentIndex],
                              ];
                              updateColumnOrder(newOrder);
                            }
                            return;
                          }
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-foreground"
                          onClick={e => e.stopPropagation()}
                        >
                          <EllipsisVertical size={4} />
                        </Button>
                      </Dropdown>
                    </div>
                  )}

                  {columnSizingEnabled && !hasPercentColumns && header.column.getCanResize() && (
                    <div
                      onDoubleClick={() => header.column.resetSize()}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={`resizer ltr absolute h-full w-[5px] cursor-col-resize select-none touch-none top-0 bg-muted right-0 ${
                        header.column.getIsResizing() ? 'bg-primary/50 opacity-100' : 'opacity-0'
                      }`}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
      )}

      <tbody className="relative z-9">
        {isLoading && skeletonRowCount > 0 ? (
          Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
            <TableSkeletonRow
              key={`skeleton-row-${rowIndex}`}
              rowIndex={rowIndex}
              cells={getHeaderGroups()[0].headers.map(header => ({
                id: header.id,
                isActionColumn: isActionColumn(header.column),
                config: header.column.columnDef.meta?.skeleton,
                className: cn(
                  header.column.columnDef.meta?.columnClassName,
                  columnPinning.left?.includes(header.id) && 'sticky z-9 sticky-cell bg-background',
                  columnPinning.right?.includes(header.id) &&
                    'sticky z-9 sticky-right bg-background',
                  isActionColumn(header.column) && 'action-column'
                ),
                style: getColStyle(header.column, header.id),
              }))}
            />
          ))
        ) : isLoading ? (
          <tr>
            <td colSpan={columns.length} className="text-center py-2 px-6">
              <div className="min-h-[200px] relative">
                <Spinner />
              </div>
            </td>
          </tr>
        ) : data.length === 0 && emptySkeletonRowCount > 0 && !isError ? (
          Array.from({ length: emptySkeletonRowCount }, (_, rowIndex) => (
            <TableSkeletonRow
              key={`empty-skeleton-row-${rowIndex}`}
              rowIndex={rowIndex}
              cells={getHeaderGroups()[0].headers.map(header => ({
                id: header.id,
                isActionColumn: isActionColumn(header.column),
                config: header.column.columnDef.meta?.skeleton,
                className: cn(
                  header.column.columnDef.meta?.columnClassName,
                  columnPinning.left?.includes(header.id) && 'sticky z-9 sticky-cell bg-background',
                  columnPinning.right?.includes(header.id) &&
                    'sticky z-9 sticky-right bg-background',
                  isActionColumn(header.column) && 'action-column'
                ),
                style: getColStyle(header.column, header.id),
              }))}
            />
          ))
        ) : data.length === 0 || isError ? (
          <tr>
            <td colSpan={columns.length} className="text-center py-2 px-6">
              {data.length === 0 && (
                <StatusCard
                  type={emptyStateType}
                  heading={noDataText}
                  text={noDataDescription}
                  className={emptyStateClassName}
                />
              )}
              {isError && (
                <p className="text-muted-foreground">{error ?? 'Something went wrong'}</p>
              )}
            </td>
          </tr>
        ) : (
          getRowModel().rows.map((row, rowIndex) => (
            <Fragment key={row.id}>
              <tr
                key={row.id}
                data-testid={`table-row-${rowIndex}`}
                className={cn(
                  'min-h-11 h-11 transition-colors overflow-hidden group z-1 relative border-b border-border divide-x divide-border',
                  row.getIsSelected()
                    ? 'bg-primary/10 hover:bg-secondary selected-row'
                    : cn(
                        'bg-background hover:bg-secondary',
                        isCardWrapper && 'bg-background dark:bg-muted'
                      ),
                  rowIndex === getRowModel().rows.length - 1 &&
                    '[&>td:first-child]:rounded-bl-[inherit] [&>td:last-child]:rounded-br-[inherit]',
                  typeof rowClassName === 'function' ? rowClassName(row, rowIndex) : rowClassName
                )}
                onClick={e => {
                  if (onRowClick) {
                    if ((e.target as HTMLElement).closest('a')) {
                      return;
                    }
                    onRowClick(row.original, row, e);
                  }
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    data-testid={`table-cell-${rowIndex}-${cell.id}`}
                    key={cell.id}
                    className={cn(
                      'min-h-11 h-11 pl-4 pr-3 py-2 overflow-hidden whitespace-nowrap overflow-ellipsis align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] text-md [&:not(:last-child)]:border-r [&:not(:last-child)]:border-border',
                      cell.column.columnDef.meta?.columnClassName,
                      columnPinning.left?.includes(cell.column.id) &&
                        cn(
                          'sticky z-9 sticky-cell',
                          row.getIsSelected()
                            ? 'bg-primary/10'
                            : cn(
                                'bg-background group-hover:bg-secondary',
                                isCardWrapper && 'dark:bg-muted dark:group-hover:bg-secondary'
                              )
                        ),
                      columnPinning.right?.includes(cell.column.id) &&
                        cn(
                          'sticky z-9 sticky-right',
                          row.getIsSelected()
                            ? 'bg-primary/10'
                            : cn(
                                'bg-background group-hover:bg-secondary',
                                isCardWrapper && 'dark:bg-muted dark:group-hover:bg-secondary'
                              )
                        ),
                      isActionColumn(cell.column) && 'action-column'
                    )}
                    data-action-column={isActionColumn(cell.column) ? 'true' : undefined}
                    style={getColStyle(cell.column, cell.column.id)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {renderAdditionalRowInfo && renderAdditionalRowInfo(row, rowIndex)}
              {row.getIsExpanded() && (
                <tr>
                  <td colSpan={columns.length}>
                    {renderExpandedRowInfo && renderExpandedRowInfo(row, rowIndex)}
                  </td>
                </tr>
              )}
            </Fragment>
          ))
        )}
      </tbody>
      {showFooter && (
        <tfoot>
          {getFooterGroups().map(footerGroup => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map(header => (
                <td
                  key={header.id}
                  className={cn(
                    'py-2 px-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] text-md'
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.footer, header.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  );

  if (!scrollEnabled) {
    return <div className={cn('overflow-hidden', wrapperClassName)}>{tableElement}</div>;
  }

  return (
    <CustomScroll
      className={cn('overflow-hidden', wrapperClassName)}
      orientation={orientation}
      infiniteScroll={infiniteScroll}
      hasMore={hasMore}
      loading={loading}
      onLoadMore={onLoadMore}
    >
      {tableElement}
    </CustomScroll>
  );
};

export default Table;
